// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Color, PenStyle } from '../../Graphics';
import { CloudVector } from './CloudVector';
import { ReloadHistoryParams } from '@shared/utils/History/ReloadHistoryParams';
import { CustomErrorClass, ErrorInformationStorage } from '../../ErrorInformationStorage';
import { LevelLine } from './LevelLine';
import { HistoricalData } from './HistoricalData';
import { LineStyleUtils } from '../../UtilsClasses/LineStyleUtils';
import { DynProperty } from '../../DynProperty';
import { CashItem } from '../History/CashItem';
import { LineStyle } from './IndicatorScriptBaseEnums';
import { InputParameterInteger } from './InputParamaterClasses/InputParameterInteger';
import { InputParameterDouble } from './InputParamaterClasses/InputParameterDouble';
import { InputParameterCombobox } from './InputParamaterClasses/InputParameterCombobox';
import { ClientType } from '@shared/utils/ClientType';
import { HashtableLightCount } from './HashtableLightCount';
import { Clouds } from './Clouds';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
// WorkItem
export class IndicatorScriptBase {
    constructor() {
        this.ProjectName = '';
        this.Comments = '';
        this.Author = '';
        this.Company = '';
        this.Copyrights = '';
        this.Version = '';
        this.DateOfCreation = '';
        this.iKey = '';
        this.LinesCount = 0;
        this.Data = null;
        this.FSeparateWindow = false;
        this.FValuesTable = {};
        this.onlyDefaultline = true;
        this.HideOnTop = false;
        this.HideLineTimeShift = false;
        this.LevelLines = new Array();
        this.customName = '';
        this.ShortName = '';
        this.FInputProperties = new Array();
        this.InputParametersStorage = {};
        this.builtInIndicatorArray = [];
        this.Indicators = null;
        this.DrawBegin = new Array();
        this._IndParameterValueMap = {};
        // FCloudBuffer, LineMarkers та UpdateIndicatorLevelLineValueHandler присвоюються в Indicator, як поля раніше створювалися там
        this.FCloudBuffer = new HashtableLightCount();
        this.SetIndicatorLine('Line', Color.Wheat, 1, LineStyle.SimpleChart);
        this.clouds = new Clouds(this);
    }
    get SeparateWindow() {
        return this.FSeparateWindow;
    }
    set SeparateWindow(value) {
        this.FSeparateWindow = value;
        this.SetValues('separateWindow', value);
    }
    SetIndicatorLine(name, color, width, style) {
        if (name == null) {
            name = '';
        }
        if (width === 0) {
            width = 1;
        }
        else {
            width = Math.abs(width);
        }
        if (this.onlyDefaultline) {
            this.onlyDefaultline = false;
            this.LinesCount--;
        }
        const prefix = 'line' + ++this.LinesCount + '.';
        this.SetValues(prefix + 'Name', name);
        this.SetValues(prefix + 'Color', color);
        this.SetValues(prefix + 'Width', width);
        this.SetValues(prefix + 'Style', style);
        this.SetValues(prefix + 'Visible', true);
        this.SetValues('linesCount', this.LinesCount);
    }
    // Access to the level lines - through the LevelLines collection
    // Property "Level" of object level line is available
    // Sets the horizontal line at the level of the Y-axis.
    SetLevelLine(name, level, color, width, style) {
        const ll = new LevelLine(name, level, color, width, style);
        ll.LineNumber = this.LinesCount + this.LevelLines.length;
        this.LevelLines.push(ll);
    }
    setHistoricalData(data) {
        this.CurrentData = data;
        this.initInternalIndicators();
    }
    SetIndexDrawBegin(index, value) {
        this.DrawBegin[index] = value;
    }
    setCustomName(name) {
        this.customName = name;
    }
    IndicatorShortName(name) {
        this.ShortName = name;
    }
    SetValues(key, value) {
        this.FValuesTable[key] = value;
    }
    GetValues() {
        return this.FValuesTable;
    }
    SetValue(par0 = undefined, par1 = undefined, par2 = undefined) {
        var _a, _b;
        let line = 0;
        let offset = 0;
        let value = 0;
        if (par0 !== undefined && par1 !== undefined && par2 === undefined) {
            line = par0;
            value = par1;
        }
        else if (par0 !== undefined && par1 !== undefined && par2 !== undefined) {
            line = par0;
            offset = par1;
            value = par2;
        }
        if (line < 0 || line >= ((_a = this.Data) === null || _a === void 0 ? void 0 : _a.Length)) {
            throw new Error('line');
        }
        const vector = (_b = this.Data) === null || _b === void 0 ? void 0 : _b.get(line);
        if (offset < 0 || offset >= vector.Length) {
            throw new Error('offset');
        }
        if (isNaN(value)) {
            value = Number.MAX_VALUE;
        }
        vector.set(vector.Length - 1 - offset, value);
    }
    GetValue(line, offset) {
        var _a, _b;
        if (line < 0 || line >= ((_a = this.Data) === null || _a === void 0 ? void 0 : _a.Length)) {
            throw new Error('line');
        }
        const vector = (_b = this.Data) === null || _b === void 0 ? void 0 : _b.get(line);
        if (offset < 0 || offset >= vector.Length) {
            throw new Error('offset');
        }
        return vector.get(vector.Length - 1 - offset);
    }
    SetLineShift(line, shift) {
        var _a;
        const vector = (_a = this.Data) === null || _a === void 0 ? void 0 : _a.get(line);
        if (!vector) {
            return;
        }
        vector.FTimeShift[line] = shift;
    }
    SetMarker(par0 = undefined, par1 = undefined, par2 = undefined) {
        var _a;
        let line = 0;
        let offset = 0;
        let color = null;
        if (par0 !== undefined && par1 !== undefined && par2 === undefined) {
            line = par0;
            color = par1;
        }
        else if (par0 !== undefined && par1 !== undefined && par2 !== undefined) {
            line = par0;
            offset = par1;
            color = par2;
        }
        // check line index
        if (line < 0 || line >= ((_a = this.Data) === null || _a === void 0 ? void 0 : _a.Length)) {
            throw new Error('line');
        }
        // check offset
        const markersBuffer = this.LineMarkers[line];
        if (offset < 0 || offset >= markersBuffer.Count) {
            throw new Error('offset');
        }
        //
        markersBuffer.SetMarker(markersBuffer.Count - 1 - offset, color);
    }
    setCloudTimeShift(buferLine, timeShift) {
        const vector = this.FCloudBuffer.Get(buferLine);
        if (isNullOrUndefined(vector)) {
            return;
        }
        vector.Shift = timeShift;
    }
    setCloudValueShift(buferLine, valueShift) {
        const vector = this.FCloudBuffer.Get(buferLine);
        if (isNullOrUndefined(vector)) {
            return;
        }
        vector.valueShift = valueShift;
    }
    setCloudColors(buferLine, color1, color2) {
        const vector = this.FCloudBuffer.Get(buferLine);
        if (isNullOrUndefined(vector)) {
            return;
        }
        vector.FirstLineColor = color1;
        vector.SecondLineColor = color2;
    }
    setCloudGradient(buferLine, gradientMethod) {
        const vector = this.FCloudBuffer.Get(buferLine);
        if (isNullOrUndefined(vector)) {
            return;
        }
        vector.GradientMethod = gradientMethod;
    }
    /// <summary>
    /// Sets the value of indicator's cloud into internal buffer;
    /// </summary>
    SetCloud(cloudNumber, index, value1, value2, color1, color2, gradientMethod, timeShift) {
        this.SetCloudBuferValue(cloudNumber, this.CurrentData.Count - 1 - index, value1, value2, color1, color2, gradientMethod, timeShift);
    }
    SetCloudBuferValue(buferLine, index, value1, value2, color1, color2, gradientMethod, timeShift) {
        let vector = this.FCloudBuffer.Get(buferLine);
        if (vector == null) {
            vector = this.FCloudBuffer.Set(buferLine, (vector = new CloudVector(this.CurrentData.Count, color1, color2, gradientMethod)));
            vector.Get(0).Add(0.0);
            vector.Get(1).Add(0.0);
        }
        vector.Get(0).set(index, value1);
        vector.Get(1).set(index, value2);
        vector.FirstLineColor = color1;
        vector.SecondLineColor = color2;
        vector.Shift = timeShift;
    }
    /// <summary>
    /// Removes redrawn parts of indicator's line within the interval set by offset
    /// </summary>
    RemoveMarker(line, offset) {
        var _a;
        // check line index
        if (line < 0 || line >= ((_a = this.Data) === null || _a === void 0 ? void 0 : _a.Length)) {
            throw new Error('line');
        }
        // check offset
        const markersBuffer = this.LineMarkers[line];
        if (offset < 0 || offset >= markersBuffer.Count) {
            throw new Error('offset');
        }
        //
        markersBuffer.RemoveMarker(markersBuffer.Count - 1 - offset);
    }
    /// <summary>
    /// Fully clears markers from line
    /// </summary>
    ClearMarkers(line) {
        var _a;
        // check line index
        if (line < 0 || line >= ((_a = this.Data) === null || _a === void 0 ? void 0 : _a.Length)) {
            throw new Error('line');
        }
        // check offset
        const markersBuffer = this.LineMarkers[line];
        markersBuffer.ClearMarkers();
    }
    InputParameter(InputParameters) {
        // feel empty fields
        // InputParameters = new InputParameterClass(InputParameters);
        const name = InputParameters.Comment || InputParameters.VariableName;
        let value = '';
        if (Object.hasOwnProperty.call(this, InputParameters.VariableName)) {
            value = this[InputParameters.VariableName];
        }
        const type = InputParameters.VariableType;
        this._IndParameterValueMap[name] = InputParameters.VariableName;
        const dp = new DynProperty(name, value, type, DynProperty.SCRIPT_PARAMETERS_GROUP);
        // if (  type === DynProperty.INTEGER || type === DynProperty.DOUBLE) {
        if (InputParameters instanceof InputParameterInteger || InputParameters instanceof InputParameterDouble) {
            dp.increment = InputParameters.Step;
            dp.decimalPlaces = InputParameters.Precision;
            dp.minimalValue = InputParameters.Minimum;
            dp.maximalValue = InputParameters.Maximum;
        }
        if (InputParameters instanceof InputParameterInteger) // принудительно сбрасываем в 0 для инта
         {
            dp.decimalPlaces = 0;
        }
        if (InputParameters instanceof InputParameterCombobox && InputParameters.Variants.length > 0) {
            const variantsArr = [];
            let i = 0;
            const len = InputParameters.Variants.length;
            for (i; i < len; i++) {
                const curVariant = InputParameters.Variants[i];
                const newItemName = Object.keys(curVariant)[0];
                const newItemValue = curVariant[newItemName];
                const newItem = {
                    type: NewContextMenuItemType.Default,
                    value: newItemValue,
                    text: newItemName
                };
                variantsArr.push(newItem);
            }
            dp.objectVariants = variantsArr;
        }
        dp.sortIndex = InputParameters.InputNumber;
        this.FInputProperties.push(dp);
    }
    // TODO.
    Properties() {
        const len = this.FInputProperties.length;
        for (let i = 0; i < len; i++) {
            const myProp = this.FInputProperties[i];
            const myPropName = myProp.name;
            const myValue = this._IndParameterValueMap[myPropName];
            myProp.value = this[myValue];
        }
        return this.FInputProperties;
    }
    // TODO.
    callBack(props) {
        const NeedRecalculate = this.setProperties(props);
        if (NeedRecalculate) {
            const indArr = this.builtInIndicatorArray;
            if (indArr) {
                this.initInternalIndicators();
            }
            this.IndicatorShortName(this.GetIndicatorShortName());
            this.setCustomName(this.getCustomName());
            this.UpdateIndexDrawBegin();
            // this.ParametersRecalculationCallBack();
        }
        return NeedRecalculate;
    }
    getCustomName() {
        return 'empty';
    }
    GetIndicatorShortName() {
        return 'empty';
    }
    UpdateIndexDrawBegin() { }
    ParametersRecalculationCallBack() { }
    // TODO умнее пока названия не придумал
    initInternalIndicators() {
        var _a, _b;
        this.clearInternalIndicators();
        this.builtInIndicatorArray = this.createInternalIndicators() || [];
        const ci = this.CurrentData ? this.CurrentData.cashItem : null;
        const indArr = this.builtInIndicatorArray;
        if (!indArr || !ci) {
            return;
        }
        const symbol = (_b = (_a = ci.Instrument) === null || _a === void 0 ? void 0 : _a.GetInteriorID()) !== null && _b !== void 0 ? _b : 'invalid_indicator_symbol';
        const tfInfo = ci.TimeFrameInfo;
        // TODO. Built-in indicator init.
        for (let i = 0, len = indArr.length; i < len; i++) {
            // Taken from PTLIndicator.InitInternalIndicator
            const ind = indArr[i];
            ind.Parent = ci;
            ind.Symbol = symbol;
            ind.TimeFrameInfo = tfInfo.Copy();
            // TODO.
            // for the range calculation
            // indicator.MainChart = MainChart;
            // indicator.OnError += new ExceptionDelegate(SubIndicatorError);
            // ind.Refresh(ci.Count(), false)
        }
    }
    AppendInternalIndicators(indicator, historicalData) {
        var _a;
        const ci = historicalData ? historicalData.cashItem : null;
        const symbol = ci.Instrument.GetInteriorID();
        const tfInfo = ci.TimeFrameInfo;
        indicator.Parent = ci;
        indicator.Symbol = symbol;
        indicator.TimeFrameInfo = tfInfo.Copy();
        (_a = this.builtInIndicatorArray) === null || _a === void 0 ? void 0 : _a.push(indicator);
    }
    Init() {
        this.IndicatorShortName(this.GetIndicatorShortName());
        this.setCustomName(this.getCustomName());
    }
    // MUST return an array of created indicators.
    createInternalIndicators() {
        return [];
    }
    NextBar() {
        const indArr = this.builtInIndicatorArray;
        if (!indArr) {
            return;
        }
        for (let i = 0, len = indArr.length; i < len; i++) {
            const ind = indArr[i];
            if (ind.NextBar) {
                ind.NextBar();
            }
        }
    }
    OnQuote() {
        const indArr = this.builtInIndicatorArray;
        if (!indArr) {
            return;
        }
        const ci = this.CurrentData ? this.CurrentData.cashItem : null;
        for (let i = 0, len = indArr.length; i < len; i++) {
            const ind = indArr[i];
            if (ind.OnQuote) {
                ind.OnQuote(ci);
            }
        }
    }
    // TODO. Refactor.
    RefreshPromise() {
        return __awaiter(this, void 0, void 0, function* () {
            const indArr = this.builtInIndicatorArray;
            if (!indArr) {
                yield Promise.resolve();
                return;
            }
            for (let i = 0, len = indArr.length; i < len; i++) {
                const ind = indArr[i];
                ind.FData.Clear();
            }
            for (let i = 0; i < this.FCloudBuffer.Count; i++) {
                this.FCloudBuffer.Clear(i);
            }
            yield Promise.resolve();
        });
    }
    AfterRefreshPromise() {
        return __awaiter(this, void 0, void 0, function* () {
            yield Promise.resolve();
        });
    }
    Dispose() {
        this.Indicators = null;
        this.clearInternalIndicators();
    }
    clearInternalIndicators() {
        const indArr = this.builtInIndicatorArray;
        if (indArr) {
            this.builtInIndicatorArray = null;
            for (let i = 0, len = indArr.length; i < len; i++) {
                const ind = indArr[i];
                if (ind === null || ind === void 0 ? void 0 : ind.Dispose) {
                    ind.Dispose();
                }
            }
        }
    }
    // TODO. IMPORTANT. unsubscribe cash item logic for chart/indicators. HistoricalData disposal.
    GetHistoricalDataPromise(request) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.CurrentData) {
                return yield Promise.reject();
            }
            const CurrentData = this.CurrentData;
            const ci = CurrentData ? CurrentData.cashItem : null;
            if (!ci) {
                return yield Promise.reject();
            }
            let tfInfo = ci.TimeFrameInfo;
            const additionalKey = request.GetAdditionalData() || tfInfo.AdditionalKey;
            tfInfo = tfInfo.Copy();
            tfInfo.Periods = request.Period * request.PeriodValue;
            tfInfo.HistoryType = request.DataType;
            tfInfo.ChartDataType = request.HistoryType;
            tfInfo.AdditionalKey = additionalKey;
            // TODO. UGLY.
            const ins = request.Instrument;
            const acc = CurrentData.Account;
            const historyParams = new ReloadHistoryParams();
            historyParams.updateWithProps({
                account: acc,
                instrument: ins,
                TimeFrameInfo: tfInfo,
                FromTime: request.fromUTC,
                ToTime: request.toUTC,
                UseDefaultInstrumentHistoryType: CurrentData.UseDefaultInstrumentHistoryType
            });
            const dataCache = ci.Parent.DataCache;
            if (!dataCache) {
                return yield Promise.reject();
            }
            try {
                const cashItem = yield CashItem.CreateWithHistory(ins, historyParams, null, true, true, ci.isRealTimeBuilding);
                return cashItem ? HistoricalData.HistoricalDataFactory(this.CurrentData._holder, ins, cashItem) : null;
            }
            catch (error) {
                const ex = new CustomErrorClass('IndicatorScriptBase error', 'IndicatorScriptBase.GetHistoricalDataPromise', 'GetHistoricalDataPromise -> CashItem.CreateWithHistory');
                ErrorInformationStorage.GetException(ex);
                return null;
            }
        });
    }
    static GetLineTypesArray() {
        const arr = LineStyleUtils.getCBItems();
        if (ClientType.IsWebDesktop()) {
            arr.push({
                type: NewContextMenuItemType.Image,
                text: 'HistogrammChart',
                value: PenStyle.HistogrammChart,
                svgImage: 'LWS-histogramm'
            });
            arr.push({
                type: NewContextMenuItemType.Image,
                text: 'HorisontalLineChart',
                value: PenStyle.HorisontalLineChart,
                svgImage: 'LWS-horizontalLine'
            });
            arr.push({
                type: NewContextMenuItemType.Image,
                text: 'Ladder',
                value: PenStyle.Ladder,
                svgImage: 'LWS-ladder'
            });
        }
        return arr;
    }
    getMinBarsCount() {
        return 0;
    }
    onPaintChart(chart, params) { }
    processMouseMove(chart) {
        return false;
    }
    setProperties(props) {
        let needRecalculate = false;
        const len = this.FInputProperties.length;
        for (let i = 0; i < len; i++) {
            const myProp = this.FInputProperties[i];
            const myPropName = myProp.name;
            const myValue = this._IndParameterValueMap[myPropName];
            const dp = DynProperty.getPropertyByName(props, myPropName);
            if (dp) {
                needRecalculate = this[myValue] !== dp.value || needRecalculate; // Було так: NeedRecalculate |= this[myValue] !== dp.value;
                this[myValue] = dp.value;
            }
        }
        return needRecalculate;
    }
}
